import React from "react";

function fmtCm(m) {
  if (m == null || !Number.isFinite(Number(m))) return "—";
  return `${(Number(m) * 100).toFixed(1)} cm`;
}

function fmtDeg(d) {
  if (d == null || !Number.isFinite(Number(d))) return "—";
  return `${Number(d).toFixed(0)}°`;
}

function meanOf(stat) {
  if (!stat || typeof stat !== "object") return null;
  return stat.mean;
}

const TIER_A_ROWS = [
  { key: "contact_height_m", label: "Contact height", fmt: fmtCm },
  { key: "elbow_height_m", label: "Elbow height", fmt: fmtCm },
  { key: "playing_distance_m", label: "Playing distance", fmt: fmtCm },
  { key: "stance_width_m", label: "Stance width", fmt: fmtCm },
  { key: "knee_bend_deg", label: "Knee bend", fmt: fmtDeg },
  {
    key: "shoulder_hip_separation_deg",
    label: "Shoulder–hip sep.",
    fmt: fmtDeg,
  },
  { key: "shoulder_tilt_m", label: "Shoulder tilt", fmt: fmtCm },
];

/**
 * Tier A/B biomechanics scorecard from lidar settings.biomechanics or compare payload.
 */
export default function Pose3DBiomechanicsPanel({
  biomechanics,
  t = (k, d) => d,
}) {
  if (!biomechanics?.tier_a) return null;
  const tierA = biomechanics.tier_a;
  const strokes = Array.isArray(biomechanics.strokes)
    ? biomechanics.strokes
    : [];

  return (
    <div className="pose3d-biomechanics-panel" style={{ marginTop: 10 }}>
      <strong>
        {t("pose3dBiomechanics", "LiDAR technique scorecard")}
      </strong>
      <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>
        {biomechanics.metric === "table"
          ? t("pose3dTableMetric", "table meters")
          : biomechanics.metric || ""}
        {biomechanics.stroke_count != null
          ? ` · ${biomechanics.stroke_count} strokes`
          : ""}
        {biomechanics.fh_topspin_count != null || biomechanics.bh_topspin_count != null
          ? ` · FH ${biomechanics.fh_topspin_count ?? 0} / BH ${biomechanics.bh_topspin_count ?? 0}`
          : ""}
      </span>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 8,
        }}
      >
        {TIER_A_ROWS.map((row) => {
          const mean = meanOf(tierA[row.key]);
          return (
            <div
              key={row.key}
              style={{
                minWidth: 110,
                padding: "6px 8px",
                background: "rgba(0,0,0,0.04)",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              <div style={{ opacity: 0.7 }}>{row.label}</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {row.fmt(mean)}
              </div>
            </div>
          );
        })}
      </div>
      {strokes.length ? (
        <details style={{ marginTop: 8, fontSize: 12 }}>
          <summary>
            {t("pose3dStrokeMetrics", "Per-stroke metrics")} ({strokes.length})
          </summary>
          <table
            style={{
              width: "100%",
              marginTop: 6,
              borderCollapse: "collapse",
              fontSize: 12,
            }}
          >
            <thead>
              <tr>
                <th align="left">t (ms)</th>
                <th align="right">Contact</th>
                <th align="right">Distance</th>
                <th align="right">Swing</th>
                <th align="right">Trunk ∠</th>
              </tr>
            </thead>
            <tbody>
              {strokes.slice(0, 24).map((s) => (
                <tr key={`${s.t_contact_ms}-${s.label}`}>
                  <td>{s.t_contact_ms}</td>
                  <td align="right">{fmtCm(s.contact_height_m)}</td>
                  <td align="right">{fmtCm(s.playing_distance_m)}</td>
                  <td align="right">{fmtCm(s.forward_swing_length_m)}</td>
                  <td align="right">
                    {fmtDeg(
                      s.trunk_rotation_peak_deg ??
                        s.on_device?.trunk_rotation_peak_deg ??
                        s.shoulder_hip_separation_deg ??
                        s.on_device?.shoulder_hip_separation_deg ??
                        s.elbow_extension_deg
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}
